<template>
  <div class="centered-container">
    <h2 class="promo-heading">
      Here are the best things for your group in ranked order.
    </h2>

    <h2 class="sub-heading">{{ instanceData.description }}</h2>

    <!-- List of Things Box -->
    <div class="content-box">
      <div class="box-heading">
        <h3>The Best Things</h3>
      </div>
      <div class="box-content">
        <ul>
          <li v-for="thing in bestThings" :key="thing.id">
            <div class="list-item">
              <span>{{ thing.thing }}</span>
              <div class="average-rating">
                <div class="star-rating-display">
                  <span
                    v-for="star in 5"
                    :key="star"
                    class="star-display"
                  >
                    <!-- Background star (gray) -->
                    <StarIcon :size="28" fill="#ddd" custom-class="star-background" />
                    <!-- Fill star (yellow) with clipping -->
                    <svg class="star-fill" viewBox="0 0 35 33" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <clipPath :id="`star-clip-${thing.id}-${star}`">
                          <rect 
                            :x="starLeft"
                            :width="star <= thing.averageRating ? starWidth : star > thing.averageRating && star - 1 < thing.averageRating ? ((thing.averageRating - (star - 1)) * starWidth) : 0"
                            height="33"
                          />
                        </clipPath>
                      </defs>
                      <polygon 
                        :points="STAR_POINTS" 
                        fill="#ffc107"
                        :clip-path="`url(#star-clip-${thing.id}-${star})`"
                      />
                    </svg>
                  </span>
                </div>
                <span class="rating-text" v-if="thing.averageRating > 0">{{ thing.averageRating.toFixed(2) }}</span>
                <span class="rating-text no-rating" v-else>No ratings</span>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="content-box" v-if="vetoedThings.length > 0">
      <div class="box-heading">
        <h3>Vetoed Things</h3>
      </div>
      <div class="box-content">
        <ul>
          <li v-for="thing in vetoedThings" :key="thing.id">
            <div class="list-item">
              <span>{{ thing.thing }}</span>
              <span class="vetoed-label">Vetoed</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import StarIcon from "@/components/StarIcon.vue";
import { STAR_POINTS } from '@/constants/stars';

export default {
  components: {
    StarIcon
  },
  props: {
    instanceData: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      bestThings: [],
      vetoedThings: [],
      STAR_POINTS: STAR_POINTS,
      // Star bounds: minX ≈ 0.99, maxX = 34, so star width ≈ 33, left offset ≈ 1
      starLeft: 0.99,
      starWidth: 33.01 // 34 - 0.99
    };
  },
  mounted() {
    this.calculateResults(this.instanceData);
  },
  watch: {
    'instanceData': {
      handler(newInstanceData) {
        this.calculateResults(newInstanceData);
      },
      deep: true,
    },
  },
  methods: {
    calculateResults(instanceData) {
      if (!instanceData || !instanceData.rankings || !instanceData.things) {
        return;
      }

      // Step 1: Calculate max vetos per participant
      // Include all participants (connected and disconnected) in the calculation
      const totalParticipants = instanceData.names ? instanceData.names.length : 1;
      const maxVetosPerParticipant = Math.floor((instanceData.things.length - 1) / totalParticipants);

      // Step 2: Collect all vetoed thing IDs, but validate veto limits
      // If a participant vetoed too many things, disregard all their vetoes
      const allVetoedThingIds = new Set();
      instanceData.names.forEach((nameObj) => {
        const userRanking = instanceData.rankings[nameObj.id];
        if (userRanking && userRanking.vetos) {
          // Check if this participant exceeded their veto limit
          if (userRanking.vetos.length <= maxVetosPerParticipant) {
            // Valid vetoes - include them
            userRanking.vetos.forEach(thingId => {
              allVetoedThingIds.add(thingId);
            });
          }
          // If they exceeded the limit, we ignore all their vetoes (but still use their ratings)
        }
      });

      // Step 3: Calculate average ratings for each thing
      // Include ratings from ALL participants (including disconnected)
      // Exclude 0-star ratings (non-votes) from the average
      const thingRatings = {}; // { thingId: { sum: number, count: number } }

      instanceData.names.forEach((nameObj) => {
        const userRanking = instanceData.rankings[nameObj.id];
        if (userRanking && userRanking.ratings) {
          Object.entries(userRanking.ratings).forEach(([thingId, stars]) => {
            // Only count ratings > 0 (exclude non-votes)
            if (stars > 0) {
              if (!thingRatings[thingId]) {
                thingRatings[thingId] = { sum: 0, count: 0 };
              }
              thingRatings[thingId].sum += stars;
              thingRatings[thingId].count += 1;
            }
          });
        }
      });

      // Step 4: Calculate averages and separate vetoed from non-vetoed
      this.bestThings = [];
      this.vetoedThings = [];

      instanceData.things.forEach((thing) => {
        const isVetoed = allVetoedThingIds.has(thing.id);
        
        if (isVetoed) {
          // Add to vetoed list (no ratings shown)
          this.vetoedThings.push({ ...thing });
        } else {
          // Calculate average rating (only for non-vetoed items)
          const ratingData = thingRatings[thing.id];
          if (ratingData && ratingData.count > 0) {
            const averageRating = ratingData.sum / ratingData.count;
            this.bestThings.push({
              ...thing,
              averageRating: averageRating
            });
          } else {
            // Item has no ratings (all 0-star or no ratings at all)
            // Still include it but with 0 average
            this.bestThings.push({
              ...thing,
              averageRating: 0
            });
          }
        }
      });

      // Step 5: Sort best things by average rating (high to low)
      this.bestThings.sort((a, b) => {
        // Items with no ratings (0 average) go to the bottom
        if (a.averageRating === 0 && b.averageRating === 0) return 0;
        if (a.averageRating === 0) return 1;
        if (b.averageRating === 0) return -1;
        return b.averageRating - a.averageRating;
      });
    },
  },
};
</script>

<style scoped>
ul {
  list-style-type: none;
  padding: 0;
  margin-top: 10px;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding: 10px 0;
}

li:last-child .list-item {
  border-bottom: none;
}

.average-rating {
  display: flex;
  align-items: center;
  gap: 10px;
}

.star-rating-display {
  display: flex;
  gap: 5px;
}

.star-display {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 28px;
}

.star-background,
.star-fill {
  position: absolute;
  left: 0;
  top: 0;
  width: 28px;
  height: 28px;
}

.rating-text {
  font-size: 18px;
  color: #666;
  font-weight: 500;
}

.rating-text.no-rating {
  color: #999;
  font-style: italic;
}

.vetoed-label {
  padding: 6px 12px;
  background-color: #e74c3c;
  color: white;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
}
</style>
